import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { GALLERY, GALLERY_CATS } from "../../data";
import { Reveal, Overline } from "./Reveal";
import { WhatsAppButton } from "./CTAs";
import { WHATSAPP_ENABLED } from "../../config";
import { BeforeAfterSlider } from "./BeforeAfterSlider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function Gallery() {
  const [cat, setCat] = useState("Tutti");
  const [all, setAll] = useState(GALLERY);
  const [pairs, setPairs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => {
      if (Array.isArray(r.data) && r.data.length) {
        const mapped = r.data.map((g) => ({
          url: `${BASE}${g.url}`,
          before: g.before_url ? `${BASE}${g.before_url}` : "",
          cat: g.category,
          label: g.label,
        }));
        setAll(mapped.filter((g) => !g.before));
        setPairs(mapped.filter((g) => g.before));
      }
    }).catch(() => {});
  }, []);

  const items = cat === "Tutti" ? all : all.filter((g) => g.cat === cat);

  return (
    <section id="galleria" className="relative bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <Reveal><Overline>Galleria lavori</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-navy text-4xl sm:text-5xl">
                Alcuni dei nostri interventi
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2">
              {GALLERY_CATS.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`gallery-filter-${c.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${
                    cat === c ? "bg-navy text-white" : "bg-surface text-ink/70 hover:bg-gray-200"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div layout className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.figure
                key={g.url}
                layout
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                data-testid={`gallery-item-${i}`}
                className={`group relative overflow-hidden rounded-xl bg-surface ${i % 5 === 0 ? "row-span-2 aspect-[3/4] lg:aspect-auto" : "aspect-[4/3]"}`}
              >
                <img
                  src={g.url}
                  alt={g.label}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                <figcaption className="absolute bottom-3 left-3 right-3 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[opacity,transform] duration-400">
                  <span className="text-xs font-semibold uppercase tracking-wider text-brand-orange">{g.cat}</span>
                  <p className="text-white text-sm font-medium">{g.label}</p>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>

        {pairs.length > 0 && (
          <div className="mt-16" data-testid="gallery-before-after">
            <Reveal><Overline>Prima / Dopo</Overline></Reveal>
            <Reveal delay={0.05}>
              <h3 className="mt-4 font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl">
                La trasformazione, trascinando
              </h3>
              <p className="mt-3 text-ink/60 text-base sm:text-lg max-w-2xl">
                Trascina il cursore per vedere il prima e il dopo dei nostri interventi.
              </p>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {pairs.map((p, i) => (
                <Reveal key={p.url} delay={(i % 3) * 0.06}>
                  <BeforeAfterSlider before={p.before} after={p.url} label={p.label} cat={p.cat} testid={`before-after-${i}`} />
                </Reveal>
              ))}
            </div>
          </div>
        )}

        <Reveal delay={0.1} className="mt-10">
          {WHATSAPP_ENABLED && <WhatsAppButton testid="gallery-whatsapp" label="Chiedi un preventivo simile" />}
        </Reveal>
      </div>
    </section>
  );
}
